import React, { useState, useEffect } from 'react';
import { X, Briefcase, Save, ChevronDown, ChevronRight, Check, Shield, Lock, Info, Crown, Users, BookOpen, ShieldCheck } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Template icon mapping by template_key
const TEMPLATE_ICONS = {
    employee: Users,
    team_manager: Users,
    admin: Shield,
    owner: Crown,
    onboarding_specialist: BookOpen,
    compliance_officer: ShieldCheck,
};

// Template color mapping by template_key
const TEMPLATE_COLORS = {
    employee: 'bg-slate-100 text-slate-600 border-slate-200',
    team_manager: 'bg-blue-100 text-blue-600 border-blue-200',
    admin: 'bg-purple-100 text-purple-600 border-purple-200',
    owner: 'bg-amber-100 text-amber-600 border-amber-200',
    onboarding_specialist: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    compliance_officer: 'bg-rose-100 text-rose-600 border-rose-200',
};

/**
 * CreateRoleModal - Modal for creating custom roles with detailed permissions
 * Now includes baseline permissions display and enhanced template selector
 */
const CreateRoleModal = ({ isOpen, onClose, onSuccess, initialData = null, isEditing = false }) => {
    const [loading, setLoading] = useState(false);
    const [permissionLoading, setPermissionLoading] = useState(false);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState([]);
    const [baselinePermissions, setBaselinePermissions] = useState([]);
    const [expandedCategories, setExpandedCategories] = useState({});
    const [showBaseline, setShowBaseline] = useState(false);
    const [roleTemplates, setRoleTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');

    // Form state
    const [formData, setFormData] = useState({
        role_name: '',
        description: '',
        permissions: {}, // Format: { "permission.key": true or "scope_value" }
    });

    // Load available permissions on mount
    useEffect(() => {
        if (isOpen) {
            fetchPermissionMetadata();
            fetchRoleTemplates();
        }
    }, [isOpen]);

    // Load initial data if editing
    useEffect(() => {
        if (initialData && isOpen) {
            setFormData({
                role_name: initialData.role_name || '',
                description: initialData.description || '',
                permissions: initialData.permissions || {},
            });
        } else if (isOpen) {
            // Reset form for create mode
            setFormData({
                role_name: '',
                description: '',
                permissions: {},
            });
        }
    }, [initialData, isOpen]);

    const fetchPermissionMetadata = async () => {
        try {
            setPermissionLoading(true);
            const response = await api.get('/permissions');
            if (response.data.success) {
                setCategories(response.data.categories);
                setBaselinePermissions(response.data.baselinePermissions || []);

                // Collapse all categories by default
                const expanded = {};
                response.data.categories.forEach(cat => {
                    expanded[cat.key] = false;
                });
                setExpandedCategories(expanded);
            }
        } catch (error) {
            console.error('Error fetching permissions:', error);
            setError('Failed to load permission options');
        } finally {
            setPermissionLoading(false);
        }
    };

    const toggleCategory = (categoryKey) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    const fetchRoleTemplates = async () => {
        try {
            const response = await api.get('/employees/roles/templates');
            if (response.data.templates) {
                setRoleTemplates(response.data.templates);
            }
        } catch (error) {
            console.error('Error fetching role templates:', error);
        }
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
        if (!templateId) return;

        const template = roleTemplates.find(t => t.id === parseInt(templateId));
        if (template) {
            setFormData(prev => ({
                ...prev,
                role_name: isEditing ? prev.role_name : template.role_name || template.name,
                description: isEditing ? prev.description : template.description || '',
                permissions: template.permissions || {},
            }));
            toast.success(`Template "${template.name}" applied!`);
        }
    };

    const handlePermissionChange = (permKey, value) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [permKey]: value
            }
        }));
    };

    const handleSelectAllCategory = (category, value) => {
        const newPermissions = { ...formData.permissions };

        category.permissions.forEach(perm => {
            if (perm.type === 'simple') {
                newPermissions[perm.key] = value === true; // true or remove/false
            } else if (perm.type === 'scoped') {
                // If selecting all, set to 'all' scope, otherwise remove/none
                newPermissions[perm.key] = value ? 'all' : 'none';
            }
        });

        if (!value) {
            // Cleanup false/none values to keep JSON clean
            category.permissions.forEach(perm => {
                delete newPermissions[perm.key];
            });
        }

        setFormData(prev => ({
            ...prev,
            permissions: newPermissions
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.role_name.trim()) {
            setError('Role name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            if (isEditing && initialData) {
                await api.put(`/employees/roles/${initialData.id}`, formData);
            } else {
                await api.post('/employees/roles', formData);
            }

            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (err) {
            console.error('Save role error:', err);
            setError(err.response?.data?.error || 'Failed to save role');
        } finally {
            setLoading(false);
        }
    };

    // Helper to get permission value (handling defaults)
    const getPermValue = (key, type) => {
        const val = formData.permissions[key];
        if (type === 'simple') return val === true;
        // For scoped, return value or 'none'
        return val || 'none';
    };

    // Count active permissions
    const activePermCount = Object.values(formData.permissions).filter(v => v && v !== 'none' && v !== false).length;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 md:p-4">
            <div className="bg-card rounded-none md:rounded-2xl shadow-2xl w-full max-w-4xl h-full md:h-[90vh] flex flex-col overflow-hidden text-sm md:text-base">
                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-border shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="hidden md:block p-2 bg-primary/10 rounded-lg">
                            <Briefcase size={24} className="text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg md:text-xl font-bold text-foreground">
                                {isEditing ? 'Edit Role' : 'Create Role'}
                            </h2>
                            <p className="hidden md:block text-sm text-muted-foreground">
                                {isEditing ? 'Modify role details and permissions' : 'Define a new role and its permissions'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
                        <X size={20} className="text-muted-foreground" />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    <form id="roleForm" onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs md:text-sm text-red-700 font-medium">
                                {error}
                            </div>
                        )}

                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div>
                                <label className="block text-xs md:text-sm font-semibold text-foreground mb-1.5 md:mb-2">
                                    Role Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.role_name}
                                    onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                                    className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm md:text-base bg-background text-foreground placeholder:text-muted-foreground"
                                    placeholder="e.g., HR Manager"
                                />
                            </div>
                            <div>
                                <label className="block text-xs md:text-sm font-semibold text-foreground mb-1.5 md:mb-2">Description</label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-3 py-2.5 md:px-4 md:py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all text-sm md:text-base bg-background text-foreground placeholder:text-muted-foreground"
                                    placeholder="Brief description of responsibilities"
                                />
                            </div>
                        </div>

                        {/* ============================================ */}
                        {/* BASELINE PERMISSIONS (Locked / Read-only)    */}
                        {/* ============================================ */}
                        {baselinePermissions.length > 0 && (
                            <div className="border border-border rounded-xl overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 px-3 py-2.5 md:px-4 md:py-3 flex items-center justify-between cursor-pointer hover:from-emerald-100 hover:to-teal-100 dark:hover:from-emerald-950/40 dark:hover:to-teal-950/40 transition-colors"
                                    onClick={() => setShowBaseline(!showBaseline)}
                                >
                                    <div className="flex items-center gap-2 md:gap-3">
                                        {showBaseline ? (
                                            <ChevronDown size={16} className="text-emerald-600 shrink-0" />
                                        ) : (
                                            <ChevronRight size={16} className="text-emerald-600 shrink-0" />
                                        )}
                                        <Lock size={14} className="text-emerald-600 shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-emerald-800 dark:text-emerald-300 text-sm md:text-base">Baseline Permissions</h4>
                                            <p className="text-xs text-emerald-600 dark:text-emerald-400 hidden md:block">
                                                Granted to all users automatically — cannot be removed
                                            </p>
                                        </div>
                                    </div>
                                    <span className="text-xs font-medium text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full shrink-0">
                                        {baselinePermissions.length} always on
                                    </span>
                                </div>

                                {showBaseline && (
                                    <div className="p-3 md:p-4 bg-emerald-50/50 dark:bg-emerald-950/10 divide-y divide-emerald-100 dark:divide-emerald-900/30">
                                        {baselinePermissions.map((perm) => (
                                            <div key={perm.key} className="py-2 md:py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="font-medium text-sm text-foreground">{perm.label}</p>
                                                    <p className="text-xs text-muted-foreground">{perm.description}</p>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <Check size={14} className="text-emerald-500" />
                                                    <span className="text-xs font-medium text-emerald-600">Always On</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ============================================ */}
                        {/* CONFIGURABLE PERMISSIONS                     */}
                        {/* ============================================ */}
                        <div className="border-t border-border pt-4 md:pt-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                                <div>
                                    <h3 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2">
                                        <Shield size={16} className="text-muted-foreground md:hidden" />
                                        <Shield size={18} className="text-muted-foreground hidden md:block" />
                                        Additional Permissions
                                    </h3>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {activePermCount} permission{activePermCount !== 1 ? 's' : ''} enabled beyond baseline
                                    </p>
                                </div>

                                {/* Template Selector — Enhanced */}
                                {roleTemplates.length > 0 && (
                                    <div className="flex items-center gap-2 w-full md:w-auto">
                                        <span className="text-xs md:text-sm text-muted-foreground whitespace-nowrap">Template:</span>
                                        <select
                                            value={selectedTemplate}
                                            onChange={(e) => handleTemplateSelect(e.target.value)}
                                            className="flex-1 md:flex-none px-3 py-2 text-xs md:text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-background text-foreground"
                                        >
                                            <option value="">Start from scratch</option>
                                            {roleTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                    {template.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* Template Quick-Pick Cards */}
                            {!isEditing && roleTemplates.length > 0 && (
                                <div className="mb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {roleTemplates.map((template) => {
                                        const IconComponent = TEMPLATE_ICONS[template.template_key] || Briefcase;
                                        const colorClasses = TEMPLATE_COLORS[template.template_key] || 'bg-muted text-muted-foreground border-border';
                                        const isSelected = selectedTemplate === String(template.id);

                                        return (
                                            <button
                                                type="button"
                                                key={template.id}
                                                onClick={() => handleTemplateSelect(String(template.id))}
                                                className={`relative p-2.5 md:p-3 rounded-xl border text-left transition-all ${isSelected
                                                        ? 'ring-2 ring-primary border-primary bg-primary/5'
                                                        : 'border-border hover:border-primary/40 hover:bg-muted/30'
                                                    }`}
                                            >
                                                {isSelected && (
                                                    <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                                        <Check size={10} className="text-white" />
                                                    </div>
                                                )}
                                                <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center mb-1.5 ${colorClasses}`}>
                                                    <IconComponent size={14} />
                                                </div>
                                                <p className="text-xs md:text-sm font-semibold text-foreground truncate">{template.name}</p>
                                                <p className="text-[10px] md:text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                                    {Object.keys(template.permissions || {}).length} permissions
                                                </p>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {permissionLoading ? (
                                <div className="space-y-4">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-16 md:h-20 bg-muted/50 rounded-xl animate-pulse"></div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3 md:space-y-4">
                                    {categories.map((category) => (
                                        <div key={category.key} className="border border-border rounded-xl overflow-hidden shadow-sm">
                                            {/* Category Header */}
                                            <div
                                                className="bg-muted/30 px-3 py-2.5 md:px-4 md:py-3 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => toggleCategory(category.key)}
                                            >
                                                <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
                                                    {expandedCategories[category.key] ? (
                                                        <ChevronDown size={16} className="text-muted-foreground shrink-0" />
                                                    ) : (
                                                        <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <h4 className="font-semibold text-foreground text-sm md:text-base truncate">{category.label}</h4>
                                                        <p className="text-xs text-muted-foreground truncate hidden md:block">{category.description}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 md:gap-2 shrink-0">
                                                    <span className="text-[10px] md:text-xs font-medium text-muted-foreground mr-1">
                                                        {category.permissions.filter(p => {
                                                            const v = formData.permissions[p.key];
                                                            return v && v !== 'none' && v !== false;
                                                        }).length}/{category.permissions.length}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectAllCategory(category, true);
                                                        }}
                                                        className="text-[10px] md:text-xs font-medium text-primary hover:text-primary/80 px-1.5 py-1 md:px-2 md:py-1 rounded hover:bg-primary/10 transition-colors"
                                                    >
                                                        All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectAllCategory(category, false);
                                                        }}
                                                        className="text-[10px] md:text-xs font-medium text-muted-foreground hover:text-red-600 px-1.5 py-1 md:px-2 md:py-1 rounded hover:bg-muted transition-colors"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Permissions List */}
                                            {expandedCategories[category.key] && (
                                                <div className="p-3 md:p-4 bg-card divide-y divide-border">
                                                    {category.permissions.map((perm) => (
                                                        <div key={perm.key} className="py-2 md:py-3 first:pt-0 last:pb-0">
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 md:gap-3">
                                                                <div>
                                                                    <p className="font-medium text-sm text-foreground">{perm.label}</p>
                                                                    <p className="text-xs text-muted-foreground">{perm.description}</p>
                                                                </div>

                                                                {/* Controls */}
                                                                <div>
                                                                    {perm.type === 'simple' ? (
                                                                        <label className="flex items-center cursor-pointer mt-1 sm:mt-0">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="sr-only peer"
                                                                                checked={getPermValue(perm.key, 'simple')}
                                                                                onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                                                                            />
                                                                            <div className="w-9 h-5 md:w-11 md:h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border after:border after:rounded-full after:h-4 after:w-4 md:after:h-5 md:after:w-5 after:transition-all peer-checked:bg-primary relative"></div>
                                                                            <span className="ml-2 text-xs md:text-sm font-medium text-muted-foreground min-w-[3rem]">
                                                                                {getPermValue(perm.key, 'simple') ? 'Allow' : 'Deny'}
                                                                            </span>
                                                                        </label>
                                                                    ) : (
                                                                        <div className="flex items-center gap-1 bg-muted p-0.5 md:p-1 rounded-lg mt-1 sm:mt-0 max-w-full overflow-x-auto">
                                                                            {['none', ...perm.options.filter(o => o !== 'none')].map((option) => (
                                                                                <button
                                                                                    type="button"
                                                                                    key={option}
                                                                                    onClick={() => handlePermissionChange(perm.key, option)}
                                                                                    className={`px-2 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-medium rounded-md transition-all whitespace-nowrap ${(getPermValue(perm.key, 'scoped') === option) || (option === 'none' && !getPermValue(perm.key, 'scoped'))
                                                                                        ? 'bg-card text-primary shadow-sm border border-border'
                                                                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                                                                                        }`}
                                                                                >
                                                                                    {option === 'all' ? 'All' :
                                                                                        option === 'team' ? 'Team' :
                                                                                            option === 'own' ? 'Own' :
                                                                                                option === 'own_team' ? 'Team' :
                                                                                                    option === 'own_created' ? 'Own' :
                                                                                                        'None'}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="p-4 md:p-6 border-t border-border bg-muted/30 flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 md:flex-none px-4 md:px-6 py-2 md:py-2.5 text-sm md:text-base text-foreground hover:bg-muted rounded-xl font-semibold transition-colors border border-border md:border-transparent bg-card md:bg-transparent"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="roleForm"
                        disabled={loading}
                        className="flex-1 md:flex-none px-4 md:px-6 py-2 md:py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-white"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} className="md:w-5 md:h-5" />
                                {isEditing ? 'Update' : 'Create'} Role
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateRoleModal;
